import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import CasinoSidebar from '@/components/CasinoSidebar';
import { ArrowRight, TrendingUp, Clock, BarChart3, DollarSign, Gamepad2, Target, Brain, Heart, Flame, ShieldAlert, Trophy, Sparkles, Timer, Compass, Swords, Gift, TrendingDown, LineChart, FileText, ShieldCheck, Rocket } from 'lucide-react';

const TOOL_META = {
  gambling_budget_calculator: { icon: DollarSign, color: '#4d7cfe', name: 'Gambling Budget Calculator', path: '/play-smart/gambling-budget-calculator' },
  cost_to_play_calculator: { icon: Gamepad2, color: '#8b5cf6', name: 'Cost2Play Calculator', path: '/play-smart/cost2play-calculator' },
  rtp_comparison_engine: { icon: BarChart3, color: '#10b981', name: 'RTP Comparison Engine', path: '/play-smart/rtp-comparison-engine' },
  bankroll_simulator: { icon: LineChart, color: '#4d7cfe', name: 'Bankroll Simulator', path: '/play-smart/bankroll-simulator' },
  bonus_terms_decoder: { icon: FileText, color: '#f59e0b', name: 'Bonus Terms Decoder', path: '/play-smart/bonus-terms-decoder' },
  pre_deposit_checklist: { icon: ShieldCheck, color: '#10b981', name: 'Pre-Deposit Checklist', path: '/play-smart/pre-deposit-checklist' },
  withdrawal_predictor: { icon: Clock, color: '#8b5cf6', name: 'Withdrawal Predictor', path: '/play-smart/withdrawal-predictor' },
  gambler_personality_quiz: { icon: Brain, color: '#4d7cfe', name: 'Gambler Personality Quiz', path: '/viral-tools/gambler-personality-quiz' },
  win_prediction_simulator: { icon: TrendingDown, color: '#ef4444', name: 'Win Prediction Simulator', path: '/viral-tools/win-prediction-simulator' },
  friend_challenge: { icon: Swords, color: '#8b5cf6', name: 'Friend Challenge', path: '/viral-tools/friend-challenge' },
  spin_wheel: { icon: Gift, color: '#f59e0b', name: 'Spin to Win', path: '/viral-tools/spin-to-win' },
  slot_volatility_matcher: { icon: Sparkles, color: '#10b981', name: 'Slot Volatility Matcher', path: '/viral-tools/slot-volatility-matcher' },
  betting_streak_tracker: { icon: Flame, color: '#f59e0b', name: 'Betting Streak Tracker', path: '/viral-tools/betting-streak-tracker' },
  mood_tracker: { icon: Heart, color: '#ef4444', name: 'Mood Tracker', path: '/emotional-wellness/mood-tracker' },
  loss_chase_detector: { icon: ShieldAlert, color: '#ef4444', name: 'Loss-Chase Detector', path: '/emotional-wellness/loss-chase-detector' },
  win_celebration: { icon: Trophy, color: '#f59e0b', name: 'Win Celebration', path: '/emotional-wellness/win-celebration' },
  session_timer: { icon: Timer, color: '#4d7cfe', name: 'Session Timer', path: '/emotional-wellness/session-timer' },
  alternative_activity: { icon: Compass, color: '#06b6d4', name: 'Alternative Activities', path: '/emotional-wellness/alternatives' },
  regret_anticipation: { icon: ShieldAlert, color: '#8b5cf6', name: 'Regret Anticipation', path: '/emotional-wellness/regret-anticipation' },
};

const FEATURED_TOOLS = [
  { key: 'rtp_comparison_engine', badge: 'Most Used', badgeColor: '#4d7cfe' },
  { key: 'gambling_budget_calculator', badge: "Editor's Pick", badgeColor: '#8b5cf6' },
  { key: 'gambler_personality_quiz', badge: 'Trending', badgeColor: '#10b981' },
  { key: 'cost_to_play_calculator' },
  { key: 'loss_chase_detector', badge: 'New', badgeColor: '#f59e0b' },
  { key: 'bankroll_simulator' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/users/stats'),
      api.get('/users/recommendations'),
      api.get('/users/activity'),
    ]).then(([statsR, recsR, actR]) => {
      if (statsR.status === 'fulfilled') setStats(statsR.value.data);
      if (recsR.status === 'fulfilled') setRecommendations(recsR.value.data.recommendations || []);
      if (actR.status === 'fulfilled') setActivity(actR.value.data.activities || []);
    });
  }, []);

  const displayName = user?.name || user?.email?.split('@')[0] || 'Player';
  const topTools = stats?.top_tools || [];
  const memberSince = stats?.member_since ? new Date(stats.member_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-8 lg:py-10" data-testid="dashboard-page">
      <div className="flex flex-col xl:flex-row gap-10">
        <div className="flex-1 min-w-0">

          {/* S1: Welcome Hero */}
          <section className="mb-10 animate-fade-in" data-testid="welcome-section">
            <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.02em' }} data-testid="welcome-message">
              Welcome back, {displayName}
            </h1>
            <p className="text-[#a0a8b8] text-base mt-2">
              {stats ? `You've used ${stats.unique_tools} tool${stats.unique_tools !== 1 ? 's' : ''} this month. Ready to explore more?` : 'Loading your stats...'}
            </p>
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-[#20263a] rounded-xl p-5 border border-white/10 text-center">
                <p className="text-3xl font-bold text-white">{stats?.unique_tools ?? '-'}</p>
                <p className="text-sm text-[#a0a8b8] mt-1">Tools Used</p>
                <p className="text-xs text-[#8b5cf6]">of 19</p>
              </div>
              <div className="bg-[#20263a] rounded-xl p-5 border border-white/10 text-center">
                <p className="text-3xl font-bold text-white">{stats?.total_calculations ?? '-'}</p>
                <p className="text-sm text-[#a0a8b8] mt-1">Calculations</p>
                <p className="text-xs text-[#8b5cf6]">this month</p>
              </div>
              <div className="bg-[#20263a] rounded-xl p-5 border border-white/10 text-center">
                <p className="text-3xl font-bold text-white">{memberSince || '-'}</p>
                <p className="text-sm text-[#a0a8b8] mt-1">Member Since</p>
                <p className="text-xs text-[#8b5cf6]">&nbsp;</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              {topTools.length > 0 ? (
                <Link to={TOOL_META[topTools[0]?.tool_name]?.path || '/tools'} className="px-6 py-3 rounded-lg bg-[#4d7cfe] text-white text-sm font-semibold hover:brightness-110 transition-all" data-testid="continue-btn">
                  Continue Where You Left Off
                </Link>
              ) : (
                <Link to="/viral-tools/gambler-personality-quiz" className="px-6 py-3 rounded-lg bg-[#4d7cfe] text-white text-sm font-semibold hover:brightness-110 transition-all" data-testid="continue-btn">
                  Take the Personality Quiz
                </Link>
              )}
              <Link to="/tools" className="px-6 py-3 rounded-lg border border-white/20 text-white text-sm font-medium hover:border-[#4d7cfe] transition-colors" data-testid="discover-btn">
                Discover New Tools <ArrowRight className="h-3.5 w-3.5 inline ml-1" />
              </Link>
            </div>
          </section>

          {/* S2: Recommended For You */}
          <section className="mb-10" data-testid="recommended-section">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-white">Recommended for You</h2>
              <p className="text-sm text-[#a0a8b8] mt-1">Based on your usage patterns and player profile</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(recommendations.length > 0 ? recommendations : ['gambler_personality_quiz', 'rtp_comparison_engine', 'gambling_budget_calculator', 'loss_chase_detector']).map((key, i) => {
                const meta = TOOL_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <Link key={i} to={meta.path} className="group bg-[#20263a] rounded-xl p-5 border border-white/10 hover:border-[#4d7cfe]/30 hover:-translate-y-0.5 transition-all" data-testid={`rec-tool-${i}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${meta.color}15` }}>
                      <Icon className="h-5 w-5" style={{ color: meta.color }} />
                    </div>
                    <h3 className="text-sm font-semibold text-white group-hover:text-[#4d7cfe] transition-colors">{meta.name}</h3>
                    <p className="text-xs text-[#64748b] mt-1">Perfect for you</p>
                    <div className="mt-3 text-xs text-[#4d7cfe] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">Try It <ArrowRight className="h-3 w-3" /></div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* S3: Your Top Tools */}
          {topTools.length > 0 && (
            <section className="mb-10" data-testid="top-tools-section">
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-white">Your Most Used Tools</h2>
                <p className="text-sm text-[#a0a8b8] mt-1">Quick access to your favorites</p>
              </div>
              <div className="space-y-3">
                {topTools.slice(0, 3).map((t, i) => {
                  const meta = TOOL_META[t.tool_name];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  const lastUsed = t.last_used ? new Date(t.last_used).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                  return (
                    <Link key={i} to={meta.path} className="flex items-center gap-5 bg-[#20263a] rounded-xl p-5 border border-white/10 hover:border-[#4d7cfe]/30 transition-all group" data-testid={`top-tool-${i}`}>
                      <div className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}15` }}>
                        <Icon className="h-7 w-7" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-white group-hover:text-[#4d7cfe] transition-colors">{meta.name}</h3>
                        <p className="text-sm text-[#a0a8b8] mt-0.5">Used {t.count} times <span className="opacity-50">|</span> Last: {lastUsed}</p>
                      </div>
                      <span className="hidden sm:flex items-center gap-1 px-4 py-2 rounded-lg border border-white/15 text-sm text-white font-medium group-hover:bg-[#4d7cfe] group-hover:border-[#4d7cfe] transition-all shrink-0">
                        Open Tool <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* S4: Featured Tools */}
          <section className="mb-10" data-testid="featured-section">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-white">Featured This Week</h2>
              <p className="text-sm text-[#a0a8b8] mt-1">Platform's most popular tools</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FEATURED_TOOLS.map((ft, i) => {
                const meta = TOOL_META[ft.key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <Link key={i} to={meta.path} className="group bg-[#20263a] rounded-xl p-6 border border-white/10 hover:border-[#4d7cfe]/30 hover:-translate-y-0.5 transition-all relative" data-testid={`featured-tool-${i}`}>
                    {ft.badge && <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full text-white" style={{ background: ft.badgeColor }}>{ft.badge}</span>}
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${meta.color}15` }}>
                      <Icon className="h-5 w-5" style={{ color: meta.color }} />
                    </div>
                    <h3 className="text-base font-semibold text-white group-hover:text-[#4d7cfe] transition-colors">{meta.name}</h3>
                    <div className="mt-3 text-xs text-[#4d7cfe] flex items-center gap-1">Try It <ArrowRight className="h-3 w-3" /></div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* S5: Explore by Category */}
          <section className="mb-10" data-testid="categories-section">
            <div className="mb-5">
              <h2 className="text-xl font-semibold text-white">Explore by Category</h2>
              <p className="text-sm text-[#a0a8b8] mt-1">Browse all tools organized by purpose</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { Icon: Target, title: 'Play Smart', count: '7 tools available', color: '#4d7cfe', filter: 'play-smart' },
                { Icon: Rocket, title: 'Viral Tools', count: '6 tools available', color: '#8b5cf6', filter: 'viral' },
                { Icon: Heart, title: 'Emotional Wellness', count: '6 tools available', color: '#10b981', filter: 'wellness' },
              ].map((cat, i) => (
                <Link key={i} to={`/tools?category=${cat.filter}`} className="bg-[#20263a] rounded-xl p-6 border border-white/10 text-center hover:border-white/20 transition-all group" style={{ borderBottom: `4px solid ${cat.color}` }} data-testid={`cat-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: `${cat.color}15` }}>
                    <cat.Icon className="h-6 w-6" style={{ color: cat.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{cat.title}</h3>
                  <p className="text-sm mt-1" style={{ color: cat.color }}>{cat.count}</p>
                  <div className="mt-4 w-full py-2.5 rounded-lg border border-white/15 text-sm text-white font-medium group-hover:bg-[#4d7cfe] group-hover:border-[#4d7cfe] transition-all">
                    View All <ArrowRight className="h-3.5 w-3.5 inline ml-1" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* S6: Recent Activity */}
          {activity.length > 0 && (
            <section data-testid="activity-section">
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-white">Your Recent Activity</h2>
                <p className="text-sm text-[#a0a8b8] mt-1">Timeline of your last tool uses</p>
              </div>
              <div className="space-y-3">
                {activity.slice(0, 5).map((act, i) => {
                  const meta = TOOL_META[act.tool_name];
                  const ts = act.timestamp ? new Date(act.timestamp) : null;
                  const timeStr = ts ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                  return (
                    <div key={i} className="bg-[#20263a] rounded-lg p-5 border-l-[3px] border-[#4d7cfe]" data-testid={`activity-${i}`}>
                      <p className="text-xs font-semibold text-[#8b5cf6] uppercase tracking-wide mb-1">{timeStr}</p>
                      <h4 className="text-base font-semibold text-white">Used {meta?.name || act.tool_name}</h4>
                      {act.calculation_data && (
                        <p className="text-sm text-[#a0a8b8] mt-0.5">
                          {act.calculation_data.result ? `Result: $${act.calculation_data.result}` : ''}
                          {act.calculation_data.archetype ? `Result: ${act.calculation_data.archetype}` : ''}
                          {act.calculation_data.amount ? `Amount: $${act.calculation_data.amount}` : ''}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* Casino Sidebar */}
        <div className="hidden xl:block w-[320px] shrink-0">
          <CasinoSidebar sourcePage="dashboard" />
        </div>
      </div>
    </div>
  );
}
